import { PAID_BEFORE_ASSIGNMENT } from './paidBeforeAssignment.js';

/**
 * DemandJet CRM pipeline stages (kanban + progress UI).
 * `id` values are persisted on Matters.Status — keep stable for existing rows.
 * `PAID_BEFORE_ASSIGNMENT` is a terminal paid alias (no separate kanban column).
 */
export const PIPELINE_STEPS = [
  {
    id: 'INTAKE_VALIDATED',
    shortLabel: 'New Case',
    label: 'New Case',
    tooltip:
      'The file has been created and is awaiting initial review and assignment.',
    nextHint: 'An attorney in the debtor’s state will be assigned.',
  },
  {
    id: 'ATTORNEY_ASSIGNED',
    shortLabel: 'Assigned',
    label: 'Assigned',
    tooltip:
      'An attorney has been assigned to your case and is drafting the demand.',
    nextHint: 'Attorney drafts the demand and prepares delivery.',
  },
  {
    id: 'CERTIFIED_MAIL_DISPATCHED',
    shortLabel: 'Sent',
    label: 'Sent',
    tooltip: 'The demand letter has been sent to your debtor via certified mail.',
    nextHint: 'Await proof of delivery.',
  },
  {
    id: 'DEMAND_DELIVERED',
    shortLabel: 'Delivered',
    label: 'Delivered',
    tooltip:
      'Proof of delivery is confirmed via certified receipt, tracking, or digital read receipt.',
    nextHint: 'Await payment or escalate if the deadline passes.',
  },
  {
    id: 'PAID',
    shortLabel: 'Paid',
    label: 'Paid',
    tooltip:
      'The debtor has fulfilled the payment requirement; the case is successfully closed.',
    nextHint: 'Recovery complete. Accounting sync runs when connected.',
  },
  {
    id: 'LITIGATION_READY',
    shortLabel: 'Unpaid',
    label: 'Unpaid',
    tooltip:
      'The demand deadline has passed without a payment agreement; the case requires litigation.',
    nextHint: 'Matter is ready for litigation or further collection action.',
  },
];

const STATUS_INDEX = {
  INTAKE_VALIDATED: 0,
  ATTORNEY_ASSIGNED: 1,
  CERTIFIED_MAIL_DISPATCHED: 2,
  DEMAND_DELIVERED: 3,
  PAID: 4,
  [PAID_BEFORE_ASSIGNMENT]: 4,
  LITIGATION_READY: 5,
  // Legacy aliases
  UNPAID_ESCALATE: 5,
};

/** Kanban / metrics column for a persisted status (aliases fold into primary steps). */
export function boardColumnIdForStatus(status) {
  const normalized = String(status || '').toUpperCase();
  if (normalized === PAID_BEFORE_ASSIGNMENT) return 'PAID';
  if (normalized === 'UNPAID_ESCALATE') return 'LITIGATION_READY';
  return normalized || 'INTAKE_VALIDATED';
}

export function statusDisplayLabel(status) {
  const normalized = String(status || '').toUpperCase();
  if (normalized === PAID_BEFORE_ASSIGNMENT) return 'Paid before assign';
  const step = PIPELINE_STEPS.find((s) => s.id === normalized);
  return step?.shortLabel || status || '—';
}

export function statusDisplayTooltip(status) {
  return getStageTooltip(status);
}

export function getPipelineIndex(status) {
  if (status == null) return 0;
  const idx = STATUS_INDEX[String(status).toUpperCase()];
  return typeof idx === 'number' ? idx : 0;
}

/**
 * After attorney assignment, client-editable case fields are locked
 * (Assigned, Sent, Delivered, Paid, Unpaid).
 */
export function isMatterRecordLocked(status) {
  return getPipelineIndex(status) >= 1;
}

/** Evidence uploads are allowed only while the demand is still a New Case. */
export function canEditMatterEvidence(status) {
  return String(status || '').trim().toUpperCase() === 'INTAKE_VALIDATED';
}

/** Evidence can be removed from any pipeline stage (add stays New Case only). */
export function canRemoveMatterEvidence(_status) {
  return true;
}

export function getStepState(stepIndex, currentIndex, status) {
  const normalized = String(status || '').toUpperCase();
  const isPaid = normalized === 'PAID' || normalized === PAID_BEFORE_ASSIGNMENT;
  const isUnpaid = normalized === 'LITIGATION_READY' || normalized === 'UNPAID_ESCALATE';

  if (isPaid && stepIndex === 4) return 'current';
  if (isUnpaid && stepIndex === 5) return 'current';

  // Paid and Unpaid are alternate endings after Delivered — never treat the other as done.
  if (isUnpaid && stepIndex === 4) return 'upcoming';
  if (isPaid && stepIndex === 5) return 'upcoming';

  if (stepIndex < currentIndex) return 'complete';
  if (stepIndex === currentIndex) return 'current';
  return 'upcoming';
}

export function getNextStepHint(status) {
  const normalized = String(status || '').toUpperCase();
  if (normalized === PAID_BEFORE_ASSIGNMENT) {
    return 'Closed with no per-demand fee — debtor paid before attorney assignment.';
  }
  if (normalized === 'PAID') {
    return PIPELINE_STEPS[4].nextHint;
  }
  if (normalized === 'LITIGATION_READY' || normalized === 'UNPAID_ESCALATE') {
    return PIPELINE_STEPS[5].nextHint;
  }
  const idx = getPipelineIndex(normalized);
  const current = PIPELINE_STEPS[idx];
  const next = PIPELINE_STEPS[idx + 1];
  if (!next) return current?.nextHint || '';
  return `Next: ${next.shortLabel || next.label}. ${current?.nextHint || ''}`.trim();
}

export function getPreviousStepLabel(status) {
  const idx = getPipelineIndex(status);
  if (idx <= 0) return null;
  const prev = PIPELINE_STEPS[idx - 1];
  return prev.shortLabel || prev.label;
}

export function getStageTooltip(statusOrId) {
  const id = String(statusOrId || '').toUpperCase();
  if (id === PAID_BEFORE_ASSIGNMENT) {
    return 'The debtor paid before an attorney was assigned. Closed with no per-demand fee.';
  }
  const step = PIPELINE_STEPS.find((s) => s.id === id);
  return step?.tooltip || '';
}
